import { useState, useCallback, useRef, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import { useApiClient } from './useApiClient';

const CHUNK_SIZE = 1500;
const PROBE_SIZE = 300;
const CONCURRENCY = 6;

const concurrentMap = async <T, R>(
	items: T[],
	limit: number,
	fn: (item: T) => Promise<R>
): Promise<R[]> => {
	const results: R[] = new Array(items.length);
	let index = 0;
	const workerCount = Math.min(limit, items.length);

	const worker = async () => {
		while (index < items.length) {
			const i = index++;
			results[i] = await fn(items[i]);
		}
	};
	await Promise.all(Array.from({ length: workerCount }, () => worker()));
	return results;
};

export const useFetchVehicles = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [vehicles, setVehicles] = useState<any[]>([]);
	const { handleApi, apiClient } = useApiClient();
	const abortControllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		return () => {
			abortControllerRef.current?.abort();
		};
	}, []);

	const fetchVehiclesRecursively = useCallback(async (): Promise<any[]> => {
		abortControllerRef.current?.abort();
		const controller = new AbortController();
		abortControllerRef.current = controller;
		const { signal } = controller;

		setVehicles([]);
		setIsLoading(true);

		let results: any[] = [];
		try {
			const { data: probeChunk, totalCount } = await handleApi(
				apiClient.vehicleController.getVehiclesFilterV2(
					undefined,
					undefined,
					0,
					PROBE_SIZE
				)
			);

			if (signal.aborted) return results;

			if (totalCount != null) {
				const remainingSkips: number[] = [];
				for (
					let skip = probeChunk.length;
					skip < totalCount;
					skip += CHUNK_SIZE
				) {
					remainingSkips.push(skip);
				}

				const chunkErrors: { skip: number; error: unknown }[] = [];

				const remainingChunks = await concurrentMap(
					remainingSkips,
					CONCURRENCY,
					async (skip) => {
						if (signal.aborted) return [] as any[];

						try {
							const { data } = await handleApi(
								apiClient.vehicleController.getVehiclesFilterV2(
									undefined,
									undefined,
									skip,
									CHUNK_SIZE
								)
							);
							return data as any[];
						} catch (err) {
							chunkErrors.push({ skip, error: err });
							return [] as any[];
						}
					}
				);

				if (chunkErrors.length > 0 && !signal.aborted) {
					console.warn('[fetchVehiclesRecursively]:', chunkErrors);
					Sentry.withScope((scope) => {
						scope.setContext('chunkedFetch', {
							failedChunks: chunkErrors.length,
							totalChunks: remainingSkips.length,
							failedSkips: chunkErrors.map((e) => e.skip),
						});
						scope.setExtra(
							'chunkErrors',
							chunkErrors.map((e) => String(e.error))
						);
						Sentry.captureException(
							new Error(
								`fetchVehiclesRecursively: ${chunkErrors.length}/${remainingSkips.length} chunks failed`
							)
						);
					});
				}

				results = [probeChunk, ...remainingChunks].flat();
			} else {
				results = [...probeChunk];
				let skip = probeChunk.length;
				let hasMore = probeChunk.length === PROBE_SIZE;

				while (hasMore && !signal.aborted) {
					const { data, totalCount: count } = await handleApi(
						apiClient.vehicleController.getVehiclesFilterV2(
							undefined,
							undefined,
							skip,
							CHUNK_SIZE
						)
					);
					results.push(...data);
					skip += CHUNK_SIZE;
					hasMore =
						count != null ? results.length < count : data.length === CHUNK_SIZE;
				}
			}

			if (!signal.aborted) {
				setVehicles(results);
			}
		} catch (err) {
			if (!signal.aborted) {
				console.error(err);
				setError(i18next.t('error_loading_data'));
			}
		} finally {
			if (!signal.aborted) {
				setIsLoading(false);
			}
		}

		return results;
	}, [apiClient, handleApi]);

	return {
		fetchVehiclesRecursively,
		isLoading,
		error,
		vehicles,
	};
};
